import { useCallback } from 'react';

import { PUBLISH_ERROR_TYPE } from 'app/components/commons/PublishModalError/PublishModalError';
import { useHidePack } from 'app/hooks/data/core/admin/packs/useHidePack';
import { usePublishPack } from 'app/hooks/data/core/admin/packs/usePublishPack';
import { useSetupPack } from 'app/hooks/data/core/admin/packs/useSetupPack';
import { useUnpublishPack } from 'app/hooks/data/core/admin/packs/useUnpublishPack';
import {
  useHideOldPackage,
  usePublishOldPackage,
} from 'app/hooks/data/packages/useSavePackage';
import { PackStatus } from 'app/typings/core/admin/packs/enum/pack';

import type { HotelPackage } from '../_types';

type Props = {
  refetchPackages: () => Promise<unknown>;
  openPublishErrorModal: (error: unknown, type: PUBLISH_ERROR_TYPE) => void;
  isLegacy: boolean;
};

export const usePackageActions = ({
  refetchPackages,
  openPublishErrorModal,
  isLegacy,
}: Props) => {
  const { mutateAsync: publishLegacyPack } = usePublishOldPackage();
  const { mutateAsync: publishPack } = usePublishPack();
  const { mutateAsync: setupPack } = useSetupPack();
  const { mutateAsync: unpublishPack } = useUnpublishPack();
  const { mutateAsync: hideLegacyPack } = useHideOldPackage();
  const { mutateAsync: hidePack } = useHidePack();

  const publishPackage = useCallback(
    async (pkg: HotelPackage) => {
      try {
        if (isLegacy === true) {
          await publishLegacyPack({ packageId: pkg.id, published: true });
        } else {
          await publishPack(pkg.id);
        }

        await refetchPackages();
      } catch (error) {
        openPublishErrorModal(error, PUBLISH_ERROR_TYPE.PACKAGE);
      }
    },
    [
      isLegacy,
      openPublishErrorModal,
      publishLegacyPack,
      publishPack,
      refetchPackages,
    ]
  );

  // Setup only exists in the new data model, so there is no legacy branch.
  const setupPackage = useCallback(
    async (pkg: HotelPackage) => {
      try {
        await setupPack(pkg.id);

        await refetchPackages();
      } catch (error) {
        openPublishErrorModal(error, PUBLISH_ERROR_TYPE.PACKAGE);
      }
    },
    [openPublishErrorModal, refetchPackages, setupPack]
  );

  const unpublishPackage = useCallback(
    async (pkg: HotelPackage) => {
      try {
        if (isLegacy) {
          if (pkg.status === PackStatus.HIDDEN) {
            await hideLegacyPack({ packageId: pkg.id, hidden: false });
          } else {
            await publishLegacyPack({
              packageId: pkg.id,
              published: false,
            });
          }
        } else {
          await unpublishPack(pkg.id);
        }

        await refetchPackages();
      } catch (error) {
        openPublishErrorModal(error, PUBLISH_ERROR_TYPE.PACKAGE);
      }
    },
    [
      isLegacy,
      refetchPackages,
      hideLegacyPack,
      publishLegacyPack,
      unpublishPack,
      openPublishErrorModal,
    ]
  );

  const hidePackage = useCallback(
    async (pkg: HotelPackage) => {
      try {
        if (isLegacy) {
          await hideLegacyPack({ packageId: pkg.id, hidden: true });
        } else {
          await hidePack(pkg.id);
        }

        await refetchPackages();
      } catch {
        // Error toast is handled by useHidePack.
      }
    },
    [isLegacy, refetchPackages, hideLegacyPack, hidePack]
  );

  return { publishPackage, setupPackage, unpublishPackage, hidePackage };
};
